// Pure PCM helpers for StackZap media (s16le, 16 kHz mono, 960 samples / 60 ms).
// No credentials, no network — unit-testable framing/resample/playback bounds.

export const SAMPLE_RATE = 16000;
export const FRAME_SAMPLES = 960;
export const FRAME_BYTES = FRAME_SAMPLES * 2;
export const FRAME_MS = 60;
export const MAX_PLAYBACK_QUEUE_FRAMES = 8;
export const MAX_PLAYBACK_HORIZON_MS = MAX_PLAYBACK_QUEUE_FRAMES * FRAME_MS;
export const MAX_PLAYBACK_HORIZON_SECONDS = MAX_PLAYBACK_HORIZON_MS / 1000;

/**
 * Fail-closed inbound size check: only exact 1920-byte PCM frames.
 * @param {number} byteLength
 * @returns {boolean}
 */
export function isExactFrameBytes(byteLength) {
  return byteLength === FRAME_BYTES;
}

/**
 * Clipped float32 [-1,1] → little-endian signed 16-bit PCM bytes.
 * @param {Float32Array|number[]} float32
 * @returns {ArrayBuffer}
 */
export function float32ToS16LE(float32) {
  const len = float32.length;
  const buf = new ArrayBuffer(len * 2);
  const view = new DataView(buf);
  for (let i = 0; i < len; i += 1) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buf;
}

/**
 * Little-endian s16 PCM → float32 [-1,1].
 * @param {ArrayBuffer|Int16Array|Uint8Array} input
 * @returns {Float32Array}
 */
export function s16LEToFloat32(input) {
  let int16;
  if (input instanceof Int16Array) {
    int16 = input;
  } else if (input instanceof ArrayBuffer) {
    int16 = new Int16Array(input);
  } else if (ArrayBuffer.isView(input)) {
    int16 = new Int16Array(
      input.buffer,
      input.byteOffset,
      Math.floor(input.byteLength / 2)
    );
  } else {
    throw new TypeError('unsupported pcm input');
  }
  const len = int16.length;
  const float32 = new Float32Array(len);
  for (let i = 0; i < len; i += 1) {
    float32[i] = int16[i] / 32768.0;
  }
  return float32;
}

/**
 * Linear resample from sourceRate to SAMPLE_RATE (16 kHz).
 * @param {Float32Array|number[]} samples
 * @param {number} sourceRate
 * @returns {Float32Array}
 */
export function resampleTo16k(samples, sourceRate) {
  const rate = Number(sourceRate);
  if (!rate || rate <= 0) {
    throw new RangeError('invalid source rate');
  }
  if (Math.abs(rate - SAMPLE_RATE) < 0.5) {
    return samples instanceof Float32Array
      ? samples
      : Float32Array.from(samples);
  }
  const ratio = rate / SAMPLE_RATE;
  const outLen = Math.max(0, Math.floor(samples.length / ratio));
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i += 1) {
    const srcIndex = i * ratio;
    const i0 = Math.floor(srcIndex);
    const i1 = Math.min(i0 + 1, samples.length - 1);
    const frac = srcIndex - i0;
    out[i] = samples[i0] * (1 - frac) + samples[i1] * frac;
  }
  return out;
}

/**
 * @param {ArrayBuffer} buffer
 * @returns {string}
 */
export function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/**
 * Accumulate mono float samples into exact FRAME_SAMPLES frames as base64 s16le.
 * @param {number[]} pendingSamples mutable residual buffer (mutated)
 * @param {Float32Array|number[]} newSamples
 * @returns {string[]} base64 frames of exactly FRAME_BYTES each
 */
export function accumulateFrames(pendingSamples, newSamples) {
  for (let i = 0; i < newSamples.length; i += 1) {
    pendingSamples.push(newSamples[i]);
  }
  const frames = [];
  while (pendingSamples.length >= FRAME_SAMPLES) {
    const chunk = pendingSamples.splice(0, FRAME_SAMPLES);
    const ab = float32ToS16LE(chunk);
    frames.push(arrayBufferToBase64(ab));
  }
  return frames;
}

/**
 * @param {string} b64
 * @returns {ArrayBuffer}
 */
export function base64ToArrayBuffer(b64) {
  const binary = atob(b64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Bounded jitter queue: push float frame; drop oldest when over max.
 * @param {Float32Array[]} queue
 * @param {Float32Array} frame
 * @param {number} [maxFrames]
 * @returns {{ dropped: number }}
 */
export function enqueuePlaybackFrame(
  queue,
  frame,
  maxFrames = MAX_PLAYBACK_QUEUE_FRAMES
) {
  let dropped = 0;
  queue.push(frame);
  while (queue.length > maxFrames) {
    queue.shift();
    dropped += 1;
  }
  return { dropped };
}

/**
 * Schedule next start time for 60 ms buffers with a small lead.
 * @param {number} audioContextCurrentTime
 * @param {number} nextPlayTime
 * @param {number} [leadSeconds]
 * @returns {number}
 */
export function nextPlaybackTime(
  audioContextCurrentTime,
  nextPlayTime,
  leadSeconds = 0.06
) {
  const minStart = audioContextCurrentTime + leadSeconds;
  if (!nextPlayTime || nextPlayTime < minStart) {
    return minStart;
  }
  return nextPlayTime;
}

/**
 * Returns a start time if the scheduled horizon stays within the bound;
 * otherwise null (caller must drop the frame — do not advance nextPlayTime).
 * Horizon: MAX_PLAYBACK_QUEUE_FRAMES * FRAME_MS from currentTime.
 *
 * @param {number} audioContextCurrentTime
 * @param {number} nextPlayTime
 * @param {number} [leadSeconds]
 * @param {number} [maxHorizonSeconds]
 * @returns {number|null}
 */
export function boundedNextPlaybackTime(
  audioContextCurrentTime,
  nextPlayTime,
  leadSeconds = FRAME_MS / 1000,
  maxHorizonSeconds = MAX_PLAYBACK_HORIZON_SECONDS
) {
  const horizon = audioContextCurrentTime + maxHorizonSeconds;
  // Already scheduled out to the bound — drop additional frames.
  if (nextPlayTime && nextPlayTime >= horizon) {
    return null;
  }
  const start = nextPlaybackTime(
    audioContextCurrentTime,
    nextPlayTime,
    leadSeconds
  );
  if (start > horizon) {
    return null;
  }
  return start;
}
